/**
 * A FOLLOW-UP CONTINUES THE DIAGRAM — it does not replace it.
 *
 * Feedback doc item 11: *"If a follow up question is related to the original/first question why
 * would the flow diagram be regenerated? Would it make more sense to append the next step to the
 * end of the diagram, or edit the original in a way that is a continuation? Basically the
 * diagramming should be smart, show true multi-step correlated reasoning."*
 *
 * What happened instead: every follow-up called the generator again and the response was assigned
 * wholesale (`setDraft(response)`), so the canvas the person had been reading was thrown away and
 * redrawn — usually with most of the same steps, in a different order, under a different name. The
 * reasoning looked discontinuous because the picture was.
 *
 * ── How "related" is decided, and why not by reading the text ────────────────
 *
 * Not with a keyword test on the follow-up. The signal is already in the DATA: a generator asked a
 * related follow-up re-emits the plan it just produced, plus whatever the new turn adds. So
 * OVERLAP decides it — if the new plan repeats any step of the old one, this is the same workflow
 * carrying on and only the genuinely new steps are appended. If it shares nothing at all, the
 * person has asked for a different workflow and the new plan replaces the old, which is the
 * behaviour they would expect from asking for something else.
 *
 * That keeps the rule tenant- and language-independent (no English phrase list compiled into the
 * bundle) and keeps it honest: the merge cannot invent a step, only keep one or add one.
 */
import type { WorkflowPlanPreview, GenerateWorkflowDraftResponse } from './workflowTypes'

type Phase = WorkflowPlanPreview['phases'][number]

/** Step identity for the merge: the title as a reader compares it, not byte-for-byte. */
function key(phase: Phase): string {
  return (phase.title ?? phase.id ?? '').trim().toLowerCase().replace(/\s+/g, ' ')
}

/**
 * Merge a freshly generated plan into the one already on the canvas.
 *
 * Returns the NEXT plan unchanged when there is nothing to continue from, or when the two plans
 * have no step in common. Otherwise returns the previous plan with the new steps appended, keeping
 * the original name and goal — the workflow is the same one, further along.
 */
export function mergeFollowUpPlan(
  previous: WorkflowPlanPreview | undefined | null,
  next: WorkflowPlanPreview,
): WorkflowPlanPreview {
  const previousPhases = previous?.phases
  if (!previous || !Array.isArray(previousPhases) || previousPhases.length === 0) return next
  const nextPhases = Array.isArray(next.phases) ? next.phases : []
  if (nextPhases.length === 0) return previous

  const seen = new Set(previousPhases.map(key))
  const shared = nextPhases.filter((phase) => seen.has(key(phase))).length
  // Nothing in common ⇒ a different workflow, not a continuation of this one.
  if (shared === 0) return next

  const added = nextPhases.filter((phase) => !seen.has(key(phase)))
  // A follow-up that adds no step must not reshuffle the picture either: the reader asked a
  // question, and the answer to it is prose, not a redrawn canvas.
  if (added.length === 0) return previous

  return { ...previous, phases: [...previousPhases, ...added] }
}

/** The same merge at the level the hook holds: a whole draft response. */
export function mergeFollowUpDraft(
  previous: GenerateWorkflowDraftResponse | undefined | null,
  next: GenerateWorkflowDraftResponse,
): GenerateWorkflowDraftResponse {
  const previousPlan = previous?.planPreview
  const nextPlan = next.planPreview
  if (!previousPlan || !nextPlan) return next
  const merged = mergeFollowUpPlan(previousPlan, nextPlan)
  if (merged === nextPlan) return next
  // Everything else on the response — the definition the backend will execute, its validation,
  // the source template — comes from the NEW response. Only the PICTURE continues, and with it
  // the plan's title, which `mergeFollowUpPlan` carries over from the previous plan so the
  // workflow does not rename itself mid-conversation.
  return { ...next, planPreview: merged }
}
